/**
 * ReportInputView — Brief entry screen for the Report Generator module.
 */
import { useEffect, useRef, useState } from 'react'
import { reports as reportsApi } from '@/api/client'
import type { ReportOptions } from '@/types'

const MAX_CHARS = 4000

const TONE_OPTIONS = [
  { value: 'professional', label: 'Professional' },
  { value: 'executive',    label: 'Executive'    },
  { value: 'analytical',   label: 'Analytical'   },
] as const

const DATA_SOURCE_OPTIONS = [
  { value: 'both',   label: 'Full search'     },
  { value: 'serp',   label: 'Standard search' },
  { value: 'tavily', label: 'Deep search'     },
] as const

const EXAMPLES = [
  'Competitive analysis of cloud security vendors in APAC for Q1 2026 — covering market share, key growth drivers, and enterprise procurement priorities.',
  'Overview of the generative AI landscape in Southeast Asia: funding rounds, leading startups, and regulatory developments through Q2 2025.',
  'Market entry analysis for a B2B SaaS company targeting mid-market logistics firms in the UK and Germany.',
]

// ── Month Picker ──────────────────────────────────────────────────────────────

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
]

const CURRENT_YEAR = new Date().getFullYear()
const YEAR_OPTIONS = Array.from({ length: CURRENT_YEAR - 2019 + 2 }, (_, i) => CURRENT_YEAR + 1 - i)

interface MonthPickerProps {
  value?:    string  // YYYY-MM
  onChange:  (v: string | undefined) => void
  label:     string
}

function MonthPicker({ value, onChange, label }: MonthPickerProps) {
  const year  = value ? parseInt(value.slice(0, 4)) : undefined
  const month = value ? parseInt(value.slice(5, 7)) : undefined

  const update = (y: number | undefined, m: number | undefined) => {
    if (y && m) onChange(`${y}-${String(m).padStart(2, '0')}`)
    else        onChange(undefined)
  }

  const selectCls = `rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-800
                     bg-white focus:outline-none focus:border-brand focus:ring-2
                     focus:ring-blue-100 transition-all cursor-pointer`

  return (
    <div className="flex-1">
      <span className="block text-[11px] text-slate-400 mb-1.5">{label}</span>
      <div className="flex gap-1.5 items-center">
        <select
          value={month ?? ''}
          onChange={e => update(year, e.target.value ? parseInt(e.target.value) : undefined)}
          className={`flex-1 ${selectCls}`}
        >
          <option value="">Month</option>
          {MONTH_NAMES.map((m, i) => (
            <option key={i + 1} value={i + 1}>{m}</option>
          ))}
        </select>
        <select
          value={year ?? ''}
          onChange={e => update(e.target.value ? parseInt(e.target.value) : undefined, month)}
          className={`w-[90px] ${selectCls}`}
        >
          <option value="">Year</option>
          {YEAR_OPTIONS.map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        {value && (
          <button
            type="button"
            onClick={() => onChange(undefined)}
            title="Clear"
            className="w-6 h-6 flex items-center justify-center rounded-full text-slate-300
                       hover:text-slate-500 hover:bg-slate-100 transition-colors text-base leading-none"
          >×</button>
        )}
      </div>
    </div>
  )
}

interface TenantInfo { name: string; label: string; has_prompt: boolean; has_vs: boolean }

interface ReportInputViewProps {
  brief:      string
  setBrief:   (v: string) => void
  options:    ReportOptions
  setOptions: (v: ReportOptions) => void
  onSubmit:   () => void
}

export default function ReportInputView({
  brief, setBrief, options, setOptions, onSubmit,
}: ReportInputViewProps) {
  const [showOptions, setShowOptions] = useState(false)
  const [tenants, setTenants]         = useState<TenantInfo[]>([])
  const ref = useRef<HTMLTextAreaElement>(null)

  const canSubmit = brief.trim().length >= 10
  const charCount = brief.length
  const charOver  = charCount > MAX_CHARS

  // Load tenant list when tenant mode is first enabled
  useEffect(() => {
    if (options.mode === 'tenant' && tenants.length === 0) {
      reportsApi.getTenants()
        .then(setTenants)
        .catch(() => setTenants([]))
    }
  }, [options.mode])

  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && canSubmit) {
      e.preventDefault()
      onSubmit()
    }
  }

  const set = <K extends keyof ReportOptions>(key: K, value: ReportOptions[K]) =>
    setOptions({ ...options, [key]: value })

  const isTenant = options.mode === 'tenant'

  return (
    <div className="flex flex-col items-center justify-start pt-14 pb-24 px-5 animate-fade-up">
      <div className="w-full max-w-[680px]">
        <div className="text-center mb-10">
          <h1 className="text-[2rem] sm:text-[2.5rem] font-bold tracking-tight text-navy-900 leading-tight">
            Turn any brief into a<br className="hidden sm:block" /> research report.
          </h1>
          <p className="mt-3 text-base text-slate-500 max-w-md mx-auto leading-relaxed">
            Paste a research brief and get a professionally formatted
            HTML&nbsp;report with a data export — in minutes.
          </p>
        </div>

        {/* Textarea */}
        <div className="relative">
          <textarea
            ref={ref}
            value={brief}
            onChange={e => setBrief(e.target.value.slice(0, MAX_CHARS))}
            onKeyDown={handleKey}
            placeholder={`Describe the report you need…\n\ne.g. We need a 2-page competitive analysis of cloud security vendors in APAC for Q1 2026, covering market share, key growth drivers, and enterprise procurement priorities.`}
            aria-label="Research brief"
            className={`w-full resize-none rounded-2xl border-2 px-5 py-4 text-[15px] leading-relaxed
              text-slate-800 placeholder:text-slate-300 bg-white shadow-card
              focus:outline-none transition-all duration-200
              ${charOver
                ? 'border-red-300 focus:border-red-400 focus:ring-2 focus:ring-red-100'
                : 'border-slate-200 focus:border-brand focus:ring-2 focus:ring-blue-100'}`}
          />
          {charCount > 0 && (
            <span className={`absolute bottom-3 right-4 text-xs tabular-nums transition-colors
              ${charOver ? 'text-red-400' : 'text-slate-300'}`}>
              {charCount.toLocaleString()} / {MAX_CHARS.toLocaleString()}
            </span>
          )}
        </div>

        {/* Example prompts */}
        {brief.length === 0 && (
          <div className="mt-4 space-y-2">
            <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Examples</p>
            {EXAMPLES.map((ex, i) => (
              <button
                key={i}
                onClick={() => { setBrief(ex); ref.current?.focus() }}
                className="w-full text-left text-sm text-slate-500 hover:text-slate-900
                           bg-white border border-slate-200 hover:border-slate-300 rounded-xl px-4 py-3
                           transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
              >
                {ex}
              </button>
            ))}
          </div>
        )}

        {/* Advanced options */}
        <div className="mt-5">
          <button
            type="button"
            onClick={() => setShowOptions(v => !v)}
            className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-700
                       transition-colors duration-150 focus-visible:outline-none
                       focus-visible:ring-2 focus-visible:ring-brand rounded"
            aria-expanded={showOptions}
          >
            <svg
              width="14" height="14" viewBox="0 0 14 14" fill="none"
              className={`transition-transform duration-200 ${showOptions ? 'rotate-90' : ''}`}
              aria-hidden="true"
            >
              <path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Advanced options
          </button>

          {showOptions && (
            <div className="mt-4 p-5 bg-white border border-slate-200 rounded-2xl space-y-5 shadow-card">

              {/* ── Mode toggle ─────────────────────────────────────────────── */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-2">Mode</label>
                <div className="flex gap-2">
                  {([
                    { value: 'research', label: '🌐 Web research' },
                    { value: 'tenant',   label: '🗄️ Knowledge base' },
                  ] as const).map(m => (
                    <button key={m.value} type="button"
                      onClick={() => set('mode', m.value)}
                      className={`px-3.5 py-1.5 rounded-lg text-sm font-medium border transition-all
                        ${options.mode === m.value
                          ? 'bg-navy-900 text-white border-navy-900'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}>
                      {m.label}
                    </button>
                  ))}
                </div>
                <p className="mt-1.5 text-xs text-slate-400">
                  {isTenant
                    ? 'Uses your OpenAI stored prompt + vector store. No web search.'
                    : 'Searches the web using multiple real-time data sources.'}
                </p>
              </div>

              {/* ── Tenant selector (tenant mode only) ──────────────────────── */}
              {isTenant && (
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-2">
                    Tenant <span className="text-slate-400 font-normal">(knowledge base)</span>
                  </label>
                  {tenants.length > 0 ? (
                    <select
                      value={options.tenant || 'default'}
                      onChange={e => set('tenant', e.target.value)}
                      className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm
                                 text-slate-800 focus:outline-none focus:border-brand
                                 focus:ring-2 focus:ring-blue-100 transition-all bg-white"
                    >
                      {tenants.map(t => (
                        <option key={t.name} value={t.name}>
                          {t.label}{!t.has_prompt && !t.has_vs ? ' ⚠ (no config)' : ''}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      No tenants configured. Add entries to <code className="font-mono">config/tenants.yaml</code> or
                      set <code className="font-mono">OPENAI_PROMPT_ID</code> / <code className="font-mono">OPENAI_VECTOR_STORE_ID</code> in <code className="font-mono">.env</code>.
                    </p>
                  )}
                </div>
              )}

              {/* ── Data source (research mode only) ────────────────────────── */}
              {!isTenant && (
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-2">Data source</label>
                  <div className="flex gap-2 flex-wrap">
                    {DATA_SOURCE_OPTIONS.map(ds => (
                      <button key={ds.value} type="button"
                        onClick={() => set('data_source', ds.value)}
                        className={`px-3.5 py-1.5 rounded-lg text-sm font-medium border transition-all
                          ${options.data_source === ds.value
                            ? 'bg-navy-900 text-white border-navy-900'
                            : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}>
                        {ds.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Time constraint (research mode only) ────────────────────── */}
              {!isTenant && (
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-2">
                    Time constraint <span className="text-slate-400 font-normal">(optional — overrides brief date range)</span>
                  </label>
                  <div className="flex gap-3 items-start">
                    <MonthPicker
                      label="From"
                      value={options.date_from}
                      onChange={v => set('date_from', v)}
                    />
                    <span className="text-slate-300 mt-7 flex-shrink-0">→</span>
                    <MonthPicker
                      label="To"
                      value={options.date_to}
                      onChange={v => set('date_to', v)}
                    />
                  </div>
                </div>
              )}

              {/* ── Pages ───────────────────────────────────────────────────── */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-2">Pages</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4].map(n => (
                    <button key={n} type="button" onClick={() => set('pages', n)}
                      className={`px-3.5 py-1.5 rounded-lg text-sm font-medium border transition-all
                        ${(options.pages ?? 2) === n
                          ? 'bg-navy-900 text-white border-navy-900'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}>
                      {n}
                    </button>
                  ))}
                </div>
              </div>

              {/* ── Tone ────────────────────────────────────────────────────── */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-2">Tone</label>
                <div className="flex gap-2 flex-wrap">
                  {TONE_OPTIONS.map(t => (
                    <button key={t.value} type="button" onClick={() => set('tone', t.value)}
                      className={`px-3.5 py-1.5 rounded-lg text-sm font-medium border transition-all
                        ${options.tone === t.value
                          ? 'bg-navy-900 text-white border-navy-900'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* ── Audience ────────────────────────────────────────────────── */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-2">
                  Audience <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={options.audience}
                  onChange={e => set('audience', e.target.value)}
                  placeholder="e.g. CISO, CFO, Board"
                  className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm
                             text-slate-800 placeholder:text-slate-300 focus:outline-none
                             focus:border-brand focus:ring-2 focus:ring-blue-100 transition-all"
                />
              </div>

              {/* ── Toggles ──────────────────────────────────────────────────── */}
              <div className="flex flex-col gap-3">
                {[
                  { key: 'skip_research', label: 'Skip web research (use internal knowledge only)', hide: isTenant },
                  { key: 'plausible',     label: 'Projected figures only',                          hide: false    },
                  { key: 'evidence',      label: 'Embed source evidence links',                hide: false    },
                ].filter(t => !t.hide).map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={Boolean(options[key as keyof ReportOptions])}
                      onChange={e => set(key as keyof ReportOptions, e.target.checked as never)}
                      className="w-4 h-4 rounded border-slate-300 text-brand focus:ring-brand"
                    />
                    <span className="text-sm text-slate-600">{label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Generate button */}
        <div className="mt-8 flex flex-col items-center gap-3">
          <button
            type="button"
            onClick={onSubmit}
            disabled={!canSubmit || charOver}
            className={`w-full sm:w-auto sm:min-w-[220px] flex items-center justify-center gap-2
              px-8 py-4 rounded-2xl text-[15px] font-semibold text-white
              transition-all duration-200 focus-visible:outline-none focus-visible:ring-2
              focus-visible:ring-brand focus-visible:ring-offset-2
              ${canSubmit && !charOver
                ? 'bg-navy-900 hover:bg-navy-800 shadow-md hover:shadow-lg active:scale-[0.98]'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8"
                    strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Generate report
          </button>
          {canSubmit && (
            <p className="text-xs text-slate-400">
              Press{' '}
              <kbd className="px-1.5 py-0.5 bg-slate-100 rounded text-slate-500 font-mono text-[11px]">
                ⌘ Enter
              </kbd>{' '}
              to generate
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
